import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useProperty } from "@/contexts/PropertyContext";
import AggregateDashboard from "@/components/AggregateDashboard";
import { Building2, Hotel, Inbox, ArrowRight, AlertCircle, Sparkles, ShieldAlert, Bot } from "lucide-react";

export default function PartnerDashboard() {
  const { user } = useAuth();
  const { isAll, selectedId } = useProperty();
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState({ negotiations: 0, requests: 0 });
  const [bookings, setBookings] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [winTips, setWinTips] = useState(null);
  const [aiDrafts, setAiDrafts] = useState({ count: 0, autoBidEnabled: false });

  useEffect(() => {
    api.get("/my/hotel").then(({ data }) => setData(data));
    api.get("/negotiations/mine").then(({ data }) => setLeads((l) => ({ ...l, negotiations: data.length })));
    api.get("/booking-requests").then(({ data }) => setLeads((l) => ({ ...l, requests: data.length })));
    api.get("/bookings/mine").then(({ data }) => setBookings(data));
    api.get("/partner/booking-alerts").then(({ data }) => setAlerts(data.items || [])).catch((e) => console.debug("fetch failed:", e?.message));
    api.get("/my/ai-drafts").then(({ data }) => {
      const enabled = (data.hotels || []).some((h) => h.auto_bid_enabled);
      setAiDrafts({ count: (data.drafts || []).length, autoBidEnabled: enabled });
    }).catch((e) => console.debug("fetch failed:", e?.message));
    // Fetch latest accepted bid and its AI congratulations + tips
    api.get("/negotiations/mine").then(async () => {
      const { data: myBids } = await api.get("/partner/won-bids-recent").catch(() => ({ data: [] }));
      if (myBids && myBids[0]) {
        try {
          const { data: tips } = await api.get(`/ai/win-tips/${myBids[0].bid_id}`);
          setWinTips({ ...tips, bid_id: myBids[0].bid_id, request_id: myBids[0].request_id });
        } catch { /* ignore */ }
      }
    });
  }, []);

  const noHotel = data === null || data === undefined || (data && !data.hotel);
  const hotel = data?.hotel;

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      {/* Colorful gradient hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-emerald-900">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 15% 30%, rgba(16,185,129,0.4) 0px, transparent 50%), radial-gradient(circle at 85% 70%, rgba(99,102,241,0.5) 0px, transparent 50%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="hw-label text-emerald-300">Hotel Partner</div>
          <h1 className="hw-head text-4xl md:text-5xl font-black text-white mt-3" data-testid="partner-heading">{user?.name}&apos;s Dashboard</h1>
          {hotel && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-slate-200">
              <span className="hw-chip border-emerald-400/60 bg-emerald-500/20 text-emerald-100 uppercase">
                <Hotel className="w-3 h-3" /> {hotel.name}
              </span>
              <span className="hw-chip border-blue-400/60 bg-blue-500/20 text-blue-100 uppercase">
                {hotel.city}
              </span>
              <span className={`hw-chip uppercase ${hotel.status === "approved" ? "border-emerald-400/60 bg-emerald-500/20 text-emerald-100" : "border-amber-400/60 bg-amber-500/20 text-amber-100"}`}>
                {hotel.status}
              </span>
              {aiDrafts.autoBidEnabled && (
                <span className="hw-chip border-purple-400/60 bg-purple-500/20 text-purple-100 uppercase">
                  <Sparkles className="w-3 h-3" /> Auto-Bid on
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">

        {/* Iter47 — Chain aggregate view (returns early when All Properties selected) */}
        {isAll ? (
          <AggregateDashboard />
        ) : (
          <PartnerSingleView
            noHotel={noHotel}
            hotel={hotel}
            leads={leads}
            bookings={bookings}
            alerts={alerts}
            winTips={winTips}
            aiDrafts={aiDrafts}
            user={user}
          />
        )}
      </div>
    </div>
  );
}

// Extracted per-property dashboard body — used when a single property is selected.
function PartnerSingleView({ noHotel, hotel, leads, bookings, alerts, winTips, aiDrafts, user }) {
  return (
    <>
        {noHotel && (
          <div className="hw-card hw-shadow-pop-amber p-7 flex items-start gap-4" data-testid="no-hotel-banner">
            <AlertCircle className="w-6 h-6 text-emerald-600 shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="hw-head font-bold text-xl text-slate-900">Register your hotel</h3>
              <p className="text-slate-600 mt-1">Add your hotel profile to start receiving leads.</p>
            </div>
            <Link to="/partner/profile" className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold px-5 py-3 inline-flex items-center" data-testid="register-hotel-btn">
              Register Hotel <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        )}

        {hotel && (
          <div className="hw-card p-6 mt-8 flex items-center gap-5 flex-wrap" data-testid="my-hotel-card">
            {hotel.photos?.[0] && <img src={hotel.photos[0]} alt={hotel.name} className="w-24 h-24 object-cover rounded-sm" />}
            <div className="flex-1 min-w-[200px]">
              <h3 className="hw-head font-bold text-2xl text-slate-900">{hotel.name}</h3>
              <div className="text-slate-500">{hotel.city}</div>
              <div className="flex items-center gap-2 mt-2">
                <span className={`hw-chip border ${hotel.status === "approved" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-emerald-300 bg-emerald-50 text-blue-700"}`}>{hotel.status}</span>
                <span className="hw-chip border-slate-300 bg-slate-50 text-slate-700 uppercase">{hotel.plan}</span>
              </div>
            </div>
            <Link to="/partner/profile" className="text-slate-900 font-bold hw-link">Edit profile →</Link>
          </div>
        )}

        {winTips && (
          <div className="mt-6 relative overflow-hidden rounded-md p-6 text-white bg-gradient-to-br from-emerald-700 via-emerald-600 to-blue-700 hw-shadow-pop" data-testid="win-tips-card">
            <div className="flex items-start gap-3">
              <Sparkles className="w-6 h-6 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <div className="hw-label text-white/80">AI congrats + tips</div>
                <h3 className="hw-head text-xl md:text-2xl font-black mt-1" data-testid="win-tips-headline">{winTips.headline}</h3>
                <div className="mt-4 grid sm:grid-cols-3 gap-3">
                  {winTips.tips?.map((t, i) => (
                    <div key={i} className="bg-white/10 backdrop-blur border border-white/20 rounded-sm p-3" data-testid={`win-tip-${i}`}>
                      <div className="font-bold text-sm">{t.title}</div>
                      <div className="text-xs text-white/85 mt-1">{t.detail}</div>
                    </div>
                  ))}
                </div>
                {winTips.fallback && <p className="text-[10px] text-white/70 mt-2">Heuristic tips — will get smarter as we learn your bidding patterns.</p>}
              </div>
            </div>
          </div>
        )}

        {hotel && (
          <Link to="/partner/ai-drafts" className="mt-6 block group" data-testid="ai-drafts-cta">
            <div className={`relative overflow-hidden rounded-md p-6 hw-shadow-pop border-2 ${aiDrafts.count > 0 ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[240px] flex items-start gap-3">
                  <div className="w-11 h-11 rounded-sm bg-gradient-to-br from-emerald-500 to-blue-600 grid place-items-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="hw-label text-emerald-700">AI Auto-Bidding{aiDrafts.autoBidEnabled ? " · ON" : " · OFF"}</div>
                    <h3 className="hw-head text-xl md:text-2xl font-black mt-1 text-slate-900">
                      {aiDrafts.count > 0
                        ? `${aiDrafts.count} AI-drafted bid${aiDrafts.count > 1 ? "s" : ""} waiting for your review`
                        : aiDrafts.autoBidEnabled
                          ? "AI is watching for new leads in your city"
                          : "Turn on Auto-Bidding — AI will draft competitive bids for you"}
                    </h3>
                    <p className="text-slate-600 text-sm mt-1">
                      {aiDrafts.count > 0
                        ? "Review the drafts, tweak the price if needed, and hit Send."
                        : "AI reads each traveller request and uses your history + market signals to draft a bid you can approve in one tap."}
                    </p>
                  </div>
                </div>
                <div className={`rounded-sm px-4 py-2 font-bold ${aiDrafts.count > 0 ? "bg-emerald-600 text-white group-hover:bg-emerald-700" : "bg-slate-900 text-white group-hover:bg-slate-800"} transition-colors`}>
                  {aiDrafts.count > 0 ? `Review ${aiDrafts.count} draft${aiDrafts.count > 1 ? "s" : ""} →` : "Configure →"}
                </div>
              </div>
            </div>
          </Link>
        )}

        {hotel && (
          <Link to={`/partner/ai?hotel=${hotel.hotel_id}`} className="mt-6 block group" data-testid="ai-studio-cta">
            <div className="relative overflow-hidden rounded-md p-6 text-white bg-gradient-to-br from-blue-800 via-blue-700 to-emerald-700 hw-shadow-pop">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 text-white/80"><Sparkles className="w-4 h-4" /><span className="hw-label">AI Studio · new</span></div>
                  <h3 className="hw-head text-2xl md:text-3xl font-black mt-2">Occupancy forecast, price optimizer & business strategy — powered by Claude.</h3>
                  <p className="text-white/85 text-sm mt-1">14-day demand, per-room price recommendations, and this week&apos;s action items — for your hotel, right now.</p>
                </div>
                <div className="bg-white/15 border border-white/30 rounded-sm px-4 py-2 font-bold group-hover:bg-white group-hover:text-blue-700 transition-colors">
                  Open AI Studio →
                </div>
              </div>
            </div>
          </Link>
        )}

        {alerts.length > 0 && (
          <div className="mt-6 hw-card p-5 border-l-4 border-l-amber-500" data-testid="suspicious-bookings-card">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="hw-head font-black text-slate-900">{alerts.length} booking{alerts.length > 1 ? "s" : ""} need a closer look</div>
                <p className="text-sm text-slate-600 mt-0.5">We flagged wallet + heavy-coupon combinations that may need your review.</p>
                <div className="mt-3 space-y-2">
                  {alerts.slice(0, 3).map((a) => (
                    <div key={a.booking_id} className="text-sm text-slate-700 flex items-center justify-between border-t border-slate-100 pt-2" data-testid={`alert-${a.booking_id}`}>
                      <div className="min-w-0">
                        <span className="font-semibold truncate">{a.customer_name}</span>
                        <span className="text-slate-500"> · {a.hotel_name}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end">
                        {a.flags.map((f) => (
                          <span key={f} className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">{f}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {alerts.length > 3 && <Link to="/partner/reservations" className="mt-3 inline-block text-xs text-blue-700 font-bold hover:underline" data-testid="alerts-view-all">View all {alerts.length} flagged bookings →</Link>}
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5 mt-8">
          <StatCard label="Active negotiations" value={leads.negotiations} to="/partner/leads" testid="stat-negotiations" />
          <StatCard label="Open lead requests" value={leads.requests} to="/partner/leads" testid="stat-requests" />
          <StatCard label="Reservations" value={bookings.length} to="/partner/reservations" testid="stat-bookings" />
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {[
            { to: "/partner/profile", title: "Profile", icon: Building2 },
            { to: "/partner/rooms", title: "Rooms", icon: Hotel },
            { to: "/partner/inventory", title: "Inventory Calendar", icon: Hotel },
            { to: "/partner/leads", title: "Leads", icon: Inbox },
            { to: "/partner/reservations", title: "Reservations", icon: Hotel },
            { to: "/negotiations", title: "Negotiations", icon: Inbox },
            { to: "/chats", title: "Chats", icon: Building2 },
          ].map((c) => (
            <Link to={c.to} key={c.to} className="hw-card p-6 hover:hw-shadow-pop transition-all hover:-translate-y-0.5 block" data-testid={`quick-${c.to.replace(/\//g,'')}`}>
              <c.icon className="w-6 h-6 text-emerald-600" />
              <h3 className="hw-head font-bold text-xl mt-4 text-slate-900">{c.title}</h3>
              <p className="text-sm text-slate-600">Manage {c.title.toLowerCase()}</p>
            </Link>
          ))}
        </div>
    </>
  );
}

function StatCard({ label, value, to, testid }) {
  return (
    <Link to={to} className="hw-card p-6 hover:hw-shadow-pop transition-all hover:-translate-y-0.5 block" data-testid={testid}>
      <div className="hw-label text-slate-500">{label}</div>
      <div className="hw-head text-5xl font-black mt-2 text-slate-900">{value}</div>
      <div className="hw-label text-blue-700 mt-2">View →</div>
    </Link>
  );
}
